import { Theme } from '@mui/material';

import { getBrandTokens } from '../../theme/getBrandTokens';
import { tokenVar } from '../../theme/brandCssVar';

export const FONT_FAMILY = 'Poppins';
// export const FONT_FAMILY = 'Poppins, sans-serif';

export const commonStyles = {
  customScrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
      backgroundColor: 'lightgray',
      borderRadius: '100px',
    },
  },
  fillerBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: (theme: Theme) => theme.custom.gap.xLarge,
    height: 'max-content',
    minHeight: (theme: Theme) => theme.custom.height['8_75'],
    border: `1px solid`,
    borderColor: (theme: Theme) => theme.custom.customColors.GREY_700,
    borderRadius: (theme: Theme) => theme.custom.borderRadius['1'],
    background: (theme: Theme) =>
      theme.custom.emptyStateBackground ??
      tokenVar(getBrandTokens(theme), 'chipDefaultGradient'),
    padding: (theme: Theme) =>
      `${theme.custom.padding.medium} ${theme.custom.padding['2_5']}`,
    position: 'relative',
  },
  fullHeight: {
    height: '100vh',
  },
  fullMinHeight: {
    minHeight: '100vh',
  },
  heading: {
    color: 'text.secondary',
    fontSize: (theme: Theme) => theme.typography.textSize.mediumXX,
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
  },
  hideScrollBar: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    msOverflowStyle: 'none' /* Internet Explorer 10+ */,
    scrollbarWidth: 'none' /* Firefox */,
  },
  darkLoaderStyles: {
    backgroundColor: 'rgba(32, 32, 32, 0.4)',
  },
  multiLineEllipsis: (noOfLines?: string) => {
    return {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: noOfLines || '2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  },
  pageContainer: {
    display: 'flex',
    padding: (theme: Theme) => theme.custom.padding.mediumX,
    flexDirection: 'column',
    marginBottom: (theme: Theme) => theme.custom.margin.medium,
    height: '100%',
    '@media (max-width: 600px)': {
      padding: (theme: Theme) => theme.custom.padding.medium,
      paddingTop: (theme: Theme) => theme.custom.padding['1_25'],
    },
  },
  pageHeading: {
    fontSize: (theme: Theme) => theme.typography.textSize.mediumX,
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
    lineHeight: (theme: Theme) => theme.typography.lineHeight['2_25'],
    color: 'text.secondary',
    marginBottom: (theme: Theme) => theme.custom.margin.mediumXX,
  },
  sectionHeading: {
    color: 'text.secondary',
    fontSize: (theme: Theme) => theme.typography.textSize.largeX,
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
    '@media(max-width: 1024px)': {
      fontSize: (theme: Theme) => theme.typography.textSize.large,
    },
    '@media(max-width: 767px)': {
      fontSize: (theme: Theme) => theme.typography.textSize.mediumXX,
    },
  },
  selectBoxCommon: {
    boxShadow: 'none',
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
    fontSize: (theme: Theme) => theme.typography.textSize['0_875'],
    color: 'text.secondary',
    '.MuiSelect-select': {
      paddingRight: 0,
      paddingLeft: (theme: Theme) => theme.custom.padding['0_375'],
    },
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
  },
  selectBoxWithBg: {
    borderRadius: (theme: Theme) => theme.custom.borderRadius.large,
    background: 'linear-gradient(0deg, #FFF 0%, #FFF 100%), #E6E9EF',
    padding: `0.625rem 0.75rem`,
    border: '1px solid',
    borderColor: 'background.fourteenary',
    '.MuiSelect-select': {
      padding: 0,
    },
  },
  selectBoxArrow: {
    color: (theme: Theme) => `${theme?.palette?.tertiary?.dark} !important`,
  },
  selectMenuDropdown: {
    borderRadius: (theme: Theme) => theme.custom.borderRadius.large,
    backgroundColor: 'background.default',
  },
  selectMenuItem: {
    fontSize: (theme: Theme) => theme.typography.textSize['0_875'],
    color: 'text.secondary',
  },
  selectedMenuItem: {
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
  },
  subHeading: {
    color: 'text.quaternary',
    fontSize: (theme: Theme) => theme.typography.textSize.mediumX,
    fontWeight: (theme: Theme) => theme.typography.fontWeight.normal,
    marginTop: (theme: Theme) => theme.custom.margin['0_375'],
  },
  subjectIcon: {
    width: '1rem',
    height: '1rem',
    filter: 'grayscale(100%) brightness(0%)',
  },
  vertialDivider: {
    border: `2px solid`,
    borderColor: 'text.quaternary',
    height: (theme: Theme) => theme.custom.height['1_25'],
  },
  exitPreviewModeBox: {
    display: 'flex',
    alignItems: 'center',
    gap: (theme: Theme) => theme.custom.gap.small,
    padding: (theme: Theme) => theme.custom.padding.medium,
  },
  exitPreviewIcon: {
    color: 'background.default',
    cursor: 'pointer',
  },
  backToCameraText: {
    color: 'background.default',
    fontSize: (theme: Theme) => theme.typography.textSize.medium,
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
  },

  // --------------- Flex Properties for Items alignment --------------

  centerEverything: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  centerVertically: {
    display: 'flex',
    alignItems: 'center',
  },

  centerHorizontally: {
    display: 'flex',
    justifyContent: 'center',
  },

  spaceBetweenItems: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  alignTop: {
    display: 'flex',
    alignItems: 'flex-start',
  },

  alignBottom: {
    display: 'flex',
    alignItems: 'flex-end',
  },

  // Test-related styles
  headingBox: {
    marginBottom: (theme: Theme) => theme.custom.margin.medium,
  },
  pageHeadingText: {
    fontSize: (theme: Theme) => theme.typography.textSize.mediumX,
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
    lineHeight: (theme: Theme) => theme.typography.lineHeight['2_25'],
    color: 'text.secondary',
  },
  listingBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: (theme: Theme) => theme.custom.gap.medium,
  },
  noDataBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: (theme: Theme) => theme.custom.padding.largeXX,
    textAlign: 'center',
    background: (theme: Theme) =>
      theme.custom.emptyStateBackground ??
      tokenVar(getBrandTokens(theme), 'chipDefaultGradient'),
    borderRadius: (theme: Theme) => theme.custom.borderRadius['1'],
  },
  sessionMonthDetailsBox: {
    display: "flex",
    gap: (theme: Theme) => theme.custom.gap["2_5"],
    marginBottom: (theme: Theme) => theme.custom.margin.large,
    "@media(max-width: 767px)": {
      flexDirection: "column",
      alignItems: "start",
      gap: "0",
    },
  },
  monthWiseListingBox: {
    flex: 1,
    paddingBottom: (theme: Theme) => theme.custom.padding["2_5"],
    display: "flex",
    gap: (theme: Theme) => theme.custom.gap["1_25"],
    flexWrap: "wrap",
    "@media (max-width: 767px)": {
      width: "100%",
      backgroundColor: "background.default",
      padding: "1rem",
      paddingTop: "0.75rem",
      borderBottomLeftRadius: "0.75rem",
      borderBottomRightRadius: "0.75rem",
      gap: (theme: Theme) => theme.custom.gap.smallMedium,
    },
  },
  monthYearAndDividerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "@media (max-width: 767px)": {
      display: "none",
    },
  },
  monthYearBox: {
    padding: (theme: Theme) => `${theme.custom.padding.small} 0`,
    "@media (max-width: 767px)": {
      width: "100%",
      padding: "0.625rem 1rem",
      backgroundColor: "background.default",
      borderTopRightRadius: "0.75rem",
      borderTopLeftRadius: "0.75rem",
    },
  },
  monthYearText: {
    color: "text.secondary",
    fontSize: (theme: Theme) => theme.typography.textSize["0_875"],
    fontWeight: (theme: Theme) => theme.typography.fontWeight.medium,
    textTransform: "uppercase",
    "@media (max-width: 767px)": {
      color: "quinary.dark",
    },
  },



  nameBox: {
    flex: 1,
    "@media (max-width: 1100px)": {
      padding: "0 0.5rem",
    },
  },
  nextQuesBox: {
    right: 0,
  },

  timelineDividerStyles: {
    height: "100%",
    minHeight: "200px",
border:'1px solid #e0e0e0',
    "@media (max-width: 767px)": {
      height: "1px",
      width: "100%",
      minHeight: "1px",
      border: 'none',
    },
  },


 
};

export const modalStyles = {
  modalBox: {
    height: '100dvh',
    overflow: 'auto',
  },
  customHeaderBoxStyles: {
    position: 'fixed',
    width: '100%',
    zIndex: 1,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    transition: 'opacity 0.5s ease',
  },
  hideHeaderRow: {
    opacity: '0',
  },
  modalContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentBox: {
    width: '60vw',
    marginTop: (theme: Theme) => theme.custom.margin['5_75'],
    marginBottom: (theme: Theme) => theme.custom.margin.mediumXX,
    '@media (max-width: 767px)': {
      width: '100vw',
      marginTop: (theme: Theme) => theme.custom.margin['8'],
      marginBottom: 0,
    },
  },
  detailsBox: {
    marginTop: (theme: Theme) => theme.custom.margin['0_87'],
  },
  modalPageStyles: {
    backgroundColor: 'background.default',
    display: 'flex',
    padding: (theme: Theme) => theme.custom.padding.largeX,
    flexDirection: 'column',
    gap: (theme: Theme) => theme.custom.gap.small,
    borderRadius: (theme: Theme) => theme.custom.borderRadius['1'],
    minHeight: '100dvh',
    paddingBottom: (theme: Theme) => theme.custom.padding['6'],
  },
};
